import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { LocalStorageEnum } from 'src/app/shared/enum.ts/local-storage.enum';
import { IUserToken } from 'src/app/shared/models/local-storage';
import { ApiResponse } from 'src/app/shared/models/api';
import {
  AdminForceResetPayload,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  VerifyResetOtpPayload,
} from 'src/app/shared/models/auth';
import { IUser } from 'src/app/shared/models/user';
import { ApiHttpService } from './api-http.service';
import { LocalStorageService } from './local-storage.service';

export interface LoginRequest {
  email: string;
  password: string;
  fcmToken?: string;
  /** When sending fcmToken (e.g. web dashboard), set so the backend stores the correct platform. */
  devicePlatform?: 'ios' | 'android' | 'web';
}

export interface LoginBody {
  access_token: string;
  refresh_token: string;
  user: IUser;
}

export type LoginResponse = ApiResponse<LoginBody>;

/**
 * Body shape returned from `auth/forgot-password`. The backend
 * confirms a user exists and returns their `id` so the OTP/reset
 * step can target the correct account without trusting client state.
 */
export interface ForgotPasswordBody {
  userId: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(ApiHttpService);
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);

  login(body: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginRequest, LoginResponse>('auth/login', body, {
      withCredentials: true,
    });
  }

  /**
   * Fires the logout request and returns the observable. Callers subscribe
   * with `takeUntilDestroyed` and call `clearSession()` on next/error so the
   * UI side-effect (storage wipe + redirect) lives in the caller's lifecycle.
   * Per implementation-guidelines §17.1 (services own URL+verb+types only).
   */
  logout(): Observable<unknown> {
    return this.http.post<Record<string, never>, unknown>('auth/logout', {});
  }

  /**
   * Clears stored credentials and bounces to the login page. Optionally
   * preserves the URL the user was on so the login flow can return them
   * after a successful sign-in. Used by the 401 branch in `AuthInterceptor`
   * and by the menu's sign-out command.
   */
  clearSession(returnUrl?: string): void {
    localStorage.clear();
    this.router.navigate(['/auth/login'], returnUrl ? { queryParams: { returnUrl } } : undefined);
  }

  setAuthToken(token: string): void {
    this.localStorageService.setItem(LocalStorageEnum.ACCESS_TOKEN, { access_token: token });
  }

  getAuthToken(): IUserToken {
    return this.localStorageService.getItem(LocalStorageEnum.ACCESS_TOKEN);
  }

  /**
   * Step 1 of the password-reset flow. Server emails an OTP and
   * returns the `userId` to thread through the next two steps.
   */
  forgotPassword(payload: ForgotPasswordPayload): Observable<ApiResponse<ForgotPasswordBody>> {
    return this.http.post<ForgotPasswordPayload, ApiResponse<ForgotPasswordBody>>('auth/forgot-password', payload);
  }

  /** Step 2 of the password-reset flow. Verifies the emailed OTP for `userId`. Returns a one-time `resetToken`. */
  verifyResetOtp(payload: VerifyResetOtpPayload): Observable<ApiResponse<{ resetToken: string }>> {
    return this.http.post<VerifyResetOtpPayload, ApiResponse<{ resetToken: string }>>('auth/verify-reset-otp', payload);
  }

  /** Step 3 of the password-reset flow. Sets the new password for `userId`. */
  resetPassword(payload: ResetPasswordPayload): Observable<ApiResponse<unknown>> {
    return this.http.post<ResetPasswordPayload, ApiResponse<unknown>>('auth/reset-password', payload);
  }

  /** Authenticated self-service password change for the current admin. */
  changePassword(payload: ChangePasswordPayload): Observable<ApiResponse<unknown>> {
    return this.http.post<ChangePasswordPayload, ApiResponse<unknown>>('auth/change-password', payload);
  }

  /**
   * SUPER_ADMIN / STORE_ADMIN force-reset for a STORE_ADMIN account.
   * The target user id is in the URL; the body carries only `newPassword`.
   */
  changeStoreUserPassword(userId: string, payload: AdminForceResetPayload): Observable<ApiResponse<unknown>> {
    return this.http.put<AdminForceResetPayload, ApiResponse<unknown>>(`user/store-user/${userId}/password`, payload);
  }

  /**
   * STORE_ADMIN force-reset for a MANAGER account inside their store.
   * The target user id is in the URL; the body carries only `newPassword`.
   */
  changeStoreManagerPassword(userId: string, payload: AdminForceResetPayload): Observable<ApiResponse<unknown>> {
    return this.http.put<AdminForceResetPayload, ApiResponse<unknown>>(
      `user/store-manager/${userId}/password`,
      payload,
    );
  }

  /** Returns the authenticated user's profile record. */
  getProfile(): Observable<ApiResponse<IUser>> {
    return this.http.get<ApiResponse<IUser>>('auth/profile');
  }
}
